import { Button, Typography } from '@wbk/ui';
import { UpRightIcon } from '@wbk/svg';
import { WebSection } from '@wbk/contentful/api';
import { parseSectionItem } from '@wbk/contentful';
import { Link, useRouter, useTranslation } from '@wbk/config';
import { useAnalytics } from '@wbk/analytics';
import { formatCustomDate } from '@/utils';
import { getEventRedirectUrl } from '@/utils/redirect';
import ScrollTitle from '../ui/ScrollTitle';
import EventCard from './EventCard';
import type { ContentfulMedia as ContentfulMediaType } from '@wbk/contentful/api';

type WebItem = {
  label: undefined;
  id: string;
  href: string;
  title: string;
  startingPrice: number;
  currencyCode: string;
  image11: ContentfulMediaType;
  schedule?: {
    openDateTime: string;
  };
  zone?: {
    zoneLogo: ContentfulMediaType;
  };
};

export default function EventSection({ section }: { section: WebSection }) {
  const { locale } = useRouter();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation(['contentful_zone', 'ui_common']);

  // Limit to 4 items only
  const items = section.contentCollection.items.slice(0, 4);

  const renderEventCard = (item: WebSection['contentCollection']['items'][0], index: number) => {
    const parsedItem = parseSectionItem({ item, locale });
    if (!parsedItem) return null;
    const parsed = parsedItem as WebItem;

    const redirectInfo = getEventRedirectUrl(item, 'slug' in item ? item.slug : '', locale, parsed);

    const openDate = parsed?.schedule?.openDateTime;
    return (
      <Button
        as={Link}
        href={redirectInfo.href}
        theme='white'
        shape='text'
        className='h-full w-full p-0'
        key={index}
        target={redirectInfo.isExternal ? '_blank' : undefined}
        onClick={() => {
          trackEvent({
            event: 'clicked_on_section_item',
            data: {
              type: item.__typename,
              slug: 'slug' in item ? item.slug : '',
              section: section.id,
              position: index,
            },
          });
        }}
        rel='noreferrer'
      >
        <EventCard
          eventType={parsedItem?.eventType}
          venueName={parsed.title}
          eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
          priceFrom={parsed.startingPrice}
          currency={parsed.currencyCode}
          image={parsed.image11}
          zoneLogo={parsed.zone?.zoneLogo}
          buttonText={parsed?.label || undefined}
        />
      </Button>
    );
  };

  return (
    <section className='container space-y-12'>
      {/* Desktop Layout - hidden on mobile */}
      <div className='hidden space-y-12 lg:block'>
        <div className='flex items-center justify-between'>
          <ScrollTitle title={section.title} />
        </div>

        {/* Desktop: 4 items in one row */}
        <div className='grid grid-cols-4 gap-4 xl:gap-8'>
          {items.map((item, index) => renderEventCard(item, index))}
        </div>

        <Button
          as={Link}
          href={`/${locale}/explore`}
          phill
          className='mx-auto flex w-fit items-center border border-white bg-transparent'
        >
          <Typography variant='body-L' className='font-semibold'>
            {t('contentful_zone:see_more')}
          </Typography>
          <UpRightIcon />
        </Button>
      </div>

      {/* Mobile Layout - hidden on desktop */}
      <div className='space-y-12 lg:hidden'>
        <div className='flex flex-col gap-4'>
          <ScrollTitle title={section.title} />
        </div>

        {/* Mobile: 4 items in 2 rows of 2 each */}
        <div className='grid grid-cols-2 gap-4'>
          {items.map((item, index) => renderEventCard(item, index))}
        </div>

        <Button
          as={Link}
          href={`/${locale}/explore`}
          phill
          className='mx-auto flex w-fit items-center border border-white bg-transparent'
        >
          <Typography className='text-body-S sm:text-body-L font-semibold'>
            {t('contentful_zone:see_more')}
          </Typography>
          <UpRightIcon />
        </Button>
      </div>
    </section>
  );
}
